import { ColumnDef } from '@tanstack/react-table';
import { SetStateAction } from 'react';
import { Pagination } from '../../../../lib/interfaces/utils';
import { cn } from '../../../../lib/utils/cn';
import { formatDateTimeDE } from '../../../../lib/utils/date';
import { ArchiveFilter } from '../../constants/archive-filter.enum';
import { ProductListItemResponseDto } from '../../types/response';
import ArchiveBadge from '../archive-badge/archive-badge';
import ArchiveRowAction from '../archive-row-action/archive-row-action';
import DataTable from '../data-table/data-table';
import ListEmptyState from '../list-empty-state/list-empty-state';

const EMPTY_STATE: Record<ArchiveFilter, { title: string; description: string }> = {
  [ArchiveFilter.ACTIVE]: { title: 'Keine aktiven Produkte', description: 'Lege ein Produkt an, um es in Angeboten zu verwenden.' },
  [ArchiveFilter.ARCHIVED]: { title: 'Keine archivierten Produkte', description: 'Archivierte Produkte erscheinen hier.' },
  [ArchiveFilter.ALL]: { title: 'Noch keine Produkte', description: 'Lege dein erstes Produkt an, um loszulegen.' }
};

export interface ProductListProps {
  products: ProductListItemResponseDto[];
  filter: ArchiveFilter;
  pagination: Pagination;
  pageCount: number;
  onPaginationChange: (next: SetStateAction<Pagination>) => void;
  isLoading: boolean;
  onEdit: (product: ProductListItemResponseDto) => void;
  onArchiveToggle: (product: ProductListItemResponseDto) => void;
}

const ProductList = ({ products, filter, pagination, pageCount, onPaginationChange, isLoading, onEdit, onArchiveToggle }: ProductListProps) => {
  const columns: ColumnDef<ProductListItemResponseDto>[] = [
    {
      id: 'title',
      header: 'Produkt',
      cell: ({ row }) => (
        <span className={cn('block truncate text-sm font-bold text-[#12282A]', row.original.isArchived && 'text-[#888]')} title={row.original.title}>
          {row.original.title}
        </span>
      )
    },
    {
      id: 'capacity',
      header: 'Kapazität',
      size: 140,
      cell: ({ row }) => (
        <span className="text-sm text-gray-700 whitespace-nowrap">
          {row.original.capacity} {row.original.capacity === 1 ? 'Slot' : 'Slots'}
        </span>
      )
    },
    {
      id: 'status',
      header: 'Status',
      size: 140,
      cell: ({ row }) => <ArchiveBadge isArchived={row.original.isArchived} />
    },
    {
      id: 'createdAt',
      header: 'Erstellt',
      size: 180,
      cell: ({ row }) => <span className="text-sm text-gray-700 whitespace-nowrap">{formatDateTimeDE(row.original.createdAt)}</span>
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <ArchiveRowAction isArchived={row.original.isArchived} onClick={() => onArchiveToggle(row.original)} />
        </div>
      )
    }
  ];

  return (
    <DataTable
      columns={columns}
      data={products}
      pagination={pagination}
      pageCount={pageCount}
      onPaginationChange={onPaginationChange}
      isLoading={isLoading}
      onRowClick={onEdit}
      emptyState={<ListEmptyState {...EMPTY_STATE[filter]} />}
    />
  );
};

export default ProductList;
